import type { ImportFieldNode } from '@erp/rpc';
import type { SchemaFieldType } from '@erp/shared-types';
import {
  ARRAY_ITEM_FIELD_NAME,
  isArrayItemPlaceholderName,
} from '@erp/shared-utils';
import {
  camelToSnake,
  isPlainObject,
  parseJsonObjectInput,
  snakeToCamel,
  toDisplayLabel,
} from './schemaParserShared';

export type ParseResult =
  { ok: true; fields: ImportFieldNode[] } | { ok: false; error: string };

const DEFAULT_NUMBER_FIELD_TYPE: SchemaFieldType = 'float';

/**
 * Thrown by `buildExtendAISchemaConfig` / `stringifyExtendAISchemaConfig`
 * when a field uses a `SchemaFieldType` that Extend AI's schema vocabulary
 * cannot represent (e.g. `currency`, `datetime`, `url`). The parser/builder
 * deliberately refuses to silently degrade these to base types on export
 * (which would produce a non-reversible round-trip after re-import).
 */
export class UnsupportedSchemaFieldTypeError extends Error {
  readonly fieldName: string;
  readonly fieldType: SchemaFieldType;

  constructor(fieldName: string, fieldType: SchemaFieldType) {
    super(
      `Field "${fieldName}" has type "${fieldType}", which Extend AI's schema vocabulary cannot express. ` +
        `Convert it to a base type (string, integer, float, boolean) before exporting. ` +
        `Supported semantic types are limited to "date" and "enum". ` +
        `See https://docs.extend.ai/product/extraction/schema for the full vocabulary.`,
    );
    this.name = 'UnsupportedSchemaFieldTypeError';
    this.fieldName = fieldName;
    this.fieldType = fieldType;
  }
}

/**
 * Returns the non-"null" base type from a JSON Schema `type` field, which may
 * be a single string ("string") or an array (["string", "null"]).
 */
function getBaseType(type: unknown): string | null {
  if (typeof type === 'string') {
    return type === 'null' ? null : type;
  }
  if (Array.isArray(type)) {
    const firstNonNull = type.find(
      (entry) => typeof entry === 'string' && entry !== 'null',
    );
    return typeof firstNonNull === 'string' ? firstNonNull : null;
  }
  return null;
}

function describe(propSchema: Record<string, unknown>): string | null {
  return typeof propSchema.description === 'string'
    ? propSchema.description
    : null;
}

function parseEnumNode(
  fieldName: string,
  label: string,
  description: string | null,
  isRequired: boolean,
  rawValues: unknown[],
): ImportFieldNode {
  const enumValues = rawValues
    .filter((value) => value !== null && value !== undefined)
    .map((value) => String(value));

  return {
    fieldName,
    fieldType: 'enum',
    label,
    description,
    isRequired,
    enumValues: enumValues.length > 0 ? enumValues : null,
  };
}

function scalarFieldType(baseType: string | null): SchemaFieldType {
  switch (baseType) {
    case 'string':
      return 'string';
    case 'integer':
      return 'integer';
    case 'number':
      return DEFAULT_NUMBER_FIELD_TYPE;
    case 'boolean':
      return 'boolean';
    default:
      return 'json';
  }
}

function parseArrayNode(
  fieldName: string,
  label: string,
  description: string | null,
  isRequired: boolean,
  propSchema: Record<string, unknown>,
): ImportFieldNode {
  const items = isPlainObject(propSchema.items) ? propSchema.items : null;
  const children: ImportFieldNode[] = [];

  if (items) {
    const itemBaseType = getBaseType(items.type);
    if (itemBaseType === 'object' && isPlainObject(items.properties)) {
      children.push(...parseObjectProperties(items));
    } else if (itemBaseType !== null) {
      // Scalar item shape (e.g. array of strings). Synthesize a single child
      // so the downstream builder emits a valid `items` schema.
      children.push(parseProperty(ARRAY_ITEM_FIELD_NAME, items, false));
    }
  }

  return {
    fieldName,
    fieldType: 'array',
    label,
    description,
    isRequired,
    children,
  };
}

function parseObjectNode(
  fieldName: string,
  label: string,
  description: string | null,
  isRequired: boolean,
  propSchema: Record<string, unknown>,
): ImportFieldNode {
  return {
    fieldName,
    fieldType: 'object',
    label,
    description,
    isRequired,
    children: parseObjectProperties(propSchema),
  };
}

function parseProperty(
  rawKey: string,
  propSchema: Record<string, unknown>,
  isRequired: boolean,
): ImportFieldNode {
  const fieldName = snakeToCamel(rawKey);
  const label = toDisplayLabel(fieldName);
  const description = describe(propSchema);

  if (Array.isArray(propSchema.enum)) {
    return parseEnumNode(
      fieldName,
      label,
      description,
      isRequired,
      propSchema.enum,
    );
  }

  if (propSchema['extend:type'] === 'date') {
    return {
      fieldName,
      fieldType: 'date',
      label,
      description,
      isRequired,
    };
  }

  const baseType = getBaseType(propSchema.type);

  if (baseType === 'object') {
    return parseObjectNode(
      fieldName,
      label,
      description,
      isRequired,
      propSchema,
    );
  }

  if (baseType === 'array') {
    return parseArrayNode(
      fieldName,
      label,
      description,
      isRequired,
      propSchema,
    );
  }

  return {
    fieldName,
    fieldType: scalarFieldType(baseType),
    label,
    description,
    isRequired,
  };
}

function parseObjectProperties(
  schemaNode: Record<string, unknown>,
): ImportFieldNode[] {
  if (!isPlainObject(schemaNode.properties)) {
    return [];
  }

  const requiredList = Array.isArray(schemaNode.required)
    ? schemaNode.required.filter(
        (entry): entry is string => typeof entry === 'string',
      )
    : [];
  const requiredSet = new Set(requiredList);

  const nodes: ImportFieldNode[] = [];
  for (const [rawKey, propValue] of Object.entries(schemaNode.properties)) {
    if (!isPlainObject(propValue)) continue;
    nodes.push(parseProperty(rawKey, propValue, requiredSet.has(rawKey)));
  }

  return nodes;
}

/**
 * True when the pasted text parses as JSON and has the Extend AI / JSON Schema
 * shape (`schema.properties` or bare `properties`) rather than being a raw
 * sample payload. Used by the import dialog to pick a parser; invalid JSON
 * returns false so the chosen parser surfaces the JSON error itself.
 */
export function isExtendAISchemaShaped(rawJson: string): boolean {
  const input = parseJsonObjectInput(rawJson, 'Paste a JSON config to import.');
  return input.ok && findRootSchema(input.value) !== null;
}

/**
 * Locate the schema root node. Supports both:
 *   { schema: { type: "object", properties: {...} } }   ← Extend AI config
 *   { type: "object", properties: {...} }                ← bare JSON Schema
 */
function findRootSchema(
  parsed: Record<string, unknown>,
): Record<string, unknown> | null {
  if (isPlainObject(parsed.schema) && isPlainObject(parsed.schema.properties)) {
    return parsed.schema;
  }
  if (isPlainObject(parsed.properties)) {
    return parsed;
  }
  return null;
}

/**
 * Parse a pasted Extend AI extraction config into a tree of import nodes.
 * The wrapper at the top of the schema (e.g. `purchase_orders: {type: "array"}`)
 * is preserved as a real top-level field so multi-entity documents extract
 * correctly. Accepts either a wrapped (`{ schema: { ... } }`) or bare
 * (`{ type: "object", properties: {...} }`) JSON Schema — see `findRootSchema`.
 *
 *   {
 *     "schema": {
 *       "type": "object",
 *       "properties": {
 *         "purchase_orders": {
 *           "type": "array",
 *           "items": {
 *             "type": "object",
 *             "properties": {
 *               "purchase_order_number": { "type": ["string", "null"] },
 *               "order_date": { "type": "string", "extend:type": "date" },
 *               "status": { "enum": ["OPEN", "CLOSED"] }
 *             },
 *             "required": ["purchase_order_number"]
 *           }
 *         }
 *       }
 *     }
 *   }
 */
export function parseExtendAISchema(rawJson: string): ParseResult {
  const input = parseJsonObjectInput(rawJson, 'Paste a JSON config to import.');
  if (!input.ok) {
    return input;
  }

  const rootSchema = findRootSchema(input.value);
  if (!rootSchema) {
    return {
      ok: false,
      error:
        'Could not find schema.properties. Expected shape: { schema: { type: "object", properties: {...} } }.',
    };
  }

  const fields = parseObjectProperties(rootSchema);
  if (fields.length === 0) {
    return { ok: false, error: 'Schema has no fields under `properties`.' };
  }

  return { ok: true, fields };
}

type ExportableSchemaField = {
  fieldName: string;
  fieldType: SchemaFieldType;
  description?: string | null;
  isRequired: boolean;
  enumValues?: string[] | null;
  sortOrder?: number;
  children?: ExportableSchemaField[];
};

type JsonSchemaNode = {
  type?: string | string[];
  description?: string;
  enum?: string[];
  properties?: Record<string, JsonSchemaNode>;
  required?: string[];
  items?: JsonSchemaNode;
  additionalProperties?: boolean;
  'extend:type'?: string;
};

export type ExtendAISchemaConfig = {
  schema: JsonSchemaNode;
  advancedOptions: {
    citationsEnabled: boolean;
  };
};

function orderedFields(
  fields: readonly ExportableSchemaField[],
): ExportableSchemaField[] {
  return fields
    .map((field, index) => ({ field, index }))
    .sort((a, b) => {
      const sortDelta =
        (a.field.sortOrder ?? a.index) - (b.field.sortOrder ?? b.index);
      return sortDelta === 0 ? a.index - b.index : sortDelta;
    })
    .map(({ field }) => field);
}

function withDescription(
  schemaNode: JsonSchemaNode,
  description: string | null | undefined,
): JsonSchemaNode {
  const trimmed = description?.trim();
  return trimmed ? { ...schemaNode, description: trimmed } : schemaNode;
}

function nullableType(type: string): string[] {
  return [type, 'null'];
}

function buildRequired(fields: readonly ExportableSchemaField[]): string[] {
  return orderedFields(fields)
    .filter((field) => field.isRequired)
    .map((field) => camelToSnake(field.fieldName));
}

function buildObjectNode(
  field: ExportableSchemaField,
  description: string | null | undefined,
): JsonSchemaNode {
  const children = field.children ?? [];
  return withDescription(
    {
      type: 'object',
      properties: buildProperties(children),
      required: buildRequired(children),
      additionalProperties: true,
    },
    description,
  );
}

function buildArrayNode(field: ExportableSchemaField): JsonSchemaNode {
  const children = field.children ?? [];
  const onlyChild = children.length === 1 ? children[0] : null;
  const shouldUseDirectItemSchema =
    onlyChild !== null &&
    (isArrayItemPlaceholderName(onlyChild.fieldName) ||
      onlyChild.fieldType === 'object' ||
      onlyChild.fieldType === 'array');
  const items = shouldUseDirectItemSchema
    ? buildFieldSchema(onlyChild, { includeDescription: true })
    : {
        type: 'object',
        properties: buildProperties(children),
        required: buildRequired(children),
        additionalProperties: true,
      };

  return withDescription(
    {
      type: 'array',
      items,
    },
    field.description,
  );
}

function buildFieldSchema(
  field: ExportableSchemaField,
  options: { includeDescription: boolean },
): JsonSchemaNode {
  const description = options.includeDescription ? field.description : null;

  switch (field.fieldType) {
    case 'object':
      return buildObjectNode(field, description);
    case 'array':
      return buildArrayNode(field);
    case 'enum': {
      // When the user has created an enum field but not yet populated any
      // values (a normal mid-edit state in the UI, also how `parseEnumNode`
      // normalizes empty inputs), emit a plain nullable string instead of
      // `enum: []`. JSON Schema reads `enum: []` as "no value satisfies",
      // which makes the schema unsatisfiable for non-null and would cause
      // Extend AI to reject every model output. Round-trip-stable with
      // `parseEnumNode`'s `enumValues: enumValues.length > 0 ? ... : null`.
      const enumValues = field.enumValues ?? [];
      if (enumValues.length === 0) {
        return withDescription({ type: nullableType('string') }, description);
      }
      return withDescription(
        {
          type: nullableType('string'),
          enum: enumValues,
        },
        description,
      );
    }
    case 'date':
      return withDescription(
        {
          type: nullableType('string'),
          'extend:type': 'date',
        },
        description,
      );
    case 'integer':
      return withDescription({ type: nullableType('integer') }, description);
    case 'float':
      return withDescription({ type: nullableType('number') }, description);
    case 'boolean':
      return withDescription({ type: nullableType('boolean') }, description);
    case 'json':
      return withDescription({}, description);
    // `text` collapses to `string` because Extend AI (and JSON Schema generally)
    // do not distinguish short vs long-form text — both extract the same way.
    // The UI-only "text" hint is intentionally lossy here.
    case 'text':
    case 'string':
      return withDescription({ type: nullableType('string') }, description);
    // Extend AI's schema vocabulary is closed: only the base types
    // (string / number / integer / boolean / object / array) plus
    // `extend:type` ∈ {`date`, `currency` (object-shaped),
    // `signature` (object-shaped)} are accepted; submitting unknown
    // `extend:type` values returns a 400. Our SchemaFieldType union
    // includes UI/DB semantic types that have no Extend AI equivalent,
    // so rather than silently collapsing them on export and degrading on
    // re-import (`currency` → `float`, `datetime`/`time`/`url`/... →
    // `string`), we reject them here. Users can convert these fields to
    // a base type before exporting. See
    // https://docs.extend.ai/product/extraction/schema for the supported
    // vocabulary.
    case 'currency':
    case 'numeric':
    case 'time':
    case 'datetime':
    case 'range':
    case 'file':
    case 'image':
    case 'url':
    case 'geospatial':
    case 'unit':
    case 'entity_reference':
    case 'document':
      throw new UnsupportedSchemaFieldTypeError(
        field.fieldName,
        field.fieldType,
      );
  }
}

function buildProperties(
  fields: readonly ExportableSchemaField[],
): Record<string, JsonSchemaNode> {
  const properties: Record<string, JsonSchemaNode> = {};
  for (const field of orderedFields(fields)) {
    properties[camelToSnake(field.fieldName)] = buildFieldSchema(field, {
      includeDescription: true,
    });
  }
  return properties;
}

export function buildExtendAISchemaConfig(
  fields: readonly ExportableSchemaField[],
): ExtendAISchemaConfig {
  return {
    schema: {
      type: 'object',
      properties: buildProperties(fields),
      required: buildRequired(fields),
      additionalProperties: true,
    },
    advancedOptions: {
      citationsEnabled: true,
    },
  };
}

export function stringifyExtendAISchemaConfig(
  fields: readonly ExportableSchemaField[],
): string {
  return JSON.stringify(buildExtendAISchemaConfig(fields), null, 2);
}
